import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '../SEOHead';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import MobileNavigation from '../Navigation/MobileNavigation';
import Footer from '../Footer/Footer';
import OptimizedImage from '../common/OptimizedImage';
import bibleChatUpdatedImage from '../../assets/bible-chat-updated.webp';

const LearnMorePage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden selection:bg-[#05a5b7] selection:text-white flex flex-col">
      <SEOHead 
        title={`${t('learnMorePage.title')} | Christ Reigns Media`}
        description={t('learnMorePage.subtitle')}
      />
      
      <DesktopNavigation />
      <MobileNavigation />
      
      <main className="flex-grow pt-24 xl:pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-8 max-w-4xl">
          
          {/* Header */}
          <div className="border-b border-gray-200 pb-8 mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4 font-sans leading-tight">
              {t('learnMorePage.title')}
            </h1>
            <p className="text-lg text-[#05a5b7] font-semibold tracking-wide">
              {t('learnMorePage.subtitle')}
            </p>
          </div>

          {/* Top Banner Image */}
          <div className="mb-12 overflow-hidden rounded-3xl border border-gray-200 shadow-md bg-gradient-to-br from-blue-50 to-indigo-100 max-h-[420px]">
            <OptimizedImage
              src={bibleChatUpdatedImage}
              alt="Bible Chat Technology Banner"
              className="w-full h-full object-cover"
              sizes="(max-width: 768px) 100vw, 896px"
            />
          </div>

          {/* Intro Section */}
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed font-light mb-12">
            <p>{t('learnMorePage.introP1')}</p>
            <p>{t('learnMorePage.introP2')}</p>
          </div>

          <div className="space-y-12 text-gray-800 leading-relaxed">
            
            {/* The Two Partners */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.partnersTitle')}
              </h2>
              <p>{t('learnMorePage.partnersIntro')}</p>
              <ul className="list-disc pl-6 space-y-2 font-medium text-gray-900">
                <li>{t('learnMorePage.partner1')}</li>
                <li>{t('learnMorePage.partner2')}</li>
              </ul>
              <p className="pt-2">{t('learnMorePage.partnersOutro')}</p>
              <p>{t('learnMorePage.partnersOutro2')}</p>
            </section>

            {/* Llama 3.1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.llamaTitle')}
              </h2>
              <p>{t('learnMorePage.llamaP1')}</p>
              <p>{t('learnMorePage.llamaP2')}</p>
              <p>{t('learnMorePage.llamaP3')}</p>
              <p>{t('learnMorePage.llamaP4')}</p>
            </section>

            {/* Groq */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.groqTitle')}
              </h2>
              <p>{t('learnMorePage.groqP1')}</p>
              <p>{t('learnMorePage.groqP2')}</p>
              <p>{t('learnMorePage.groqP3')}</p>
            </section>

            {/* Step by Step */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.stepTitle')}
              </h2>

              {/* Step 1 */}
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-gray-900">{t('learnMorePage.step1Title')}</h3>
                <p>{t('learnMorePage.step1Text')}</p>
                <div className="bg-gray-50 border-l-4 border-[#05a5b7] p-5 my-4 rounded-r-xl font-mono text-sm leading-relaxed text-gray-700 whitespace-pre-line shadow-inner max-h-96 overflow-y-auto">
                  {t('learnMorePage.systemPromptQuote')}
                </div>
                <p>{t('learnMorePage.step1Outro')}</p>
              </div>

              {/* Step 2 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('learnMorePage.step2Title')}</h3>
                <p>{t('learnMorePage.step2Text')}</p>
              </div>

              {/* Step 3 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('learnMorePage.step3Title')}</h3>
                <p>{t('learnMorePage.step3Text')}</p>
              </div>

              {/* Step 4 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('learnMorePage.step4Title')}</h3>
                <p>{t('learnMorePage.step4Text')}</p>
              </div>

              {/* Step 5 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('learnMorePage.step5Title')}</h3>
                <p>{t('learnMorePage.step5Text')}</p>
              </div>
            </section>

            {/* Looking up Verses */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.lookupTitle')}
              </h2>
              <p>{t('learnMorePage.lookupText1')}</p>
              <p>{t('learnMorePage.lookupText2')}</p>
            </section>

            {/* Can it be wrong */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.wrongTitle')}
              </h2>
              <p>{t('learnMorePage.wrongText1')}</p>
              <p>{t('learnMorePage.wrongText2')}</p>
              <p>{t('learnMorePage.wrongText3')}</p>
              <p>{t('learnMorePage.wrongText4')}</p>
            </section>

            {/* Why we built it this way */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.whyTitle')}
              </h2>
              <p>{t('learnMorePage.whyIntro')}</p>
              
              {/* Features Table */}
              <div className="overflow-x-auto border border-gray-200 rounded-2xl shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                        {t('learnMorePage.tableHeaderFeature')}
                      </th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                        {t('learnMorePage.tableHeaderMatters')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-150">
                    <tr>
                      <td className="px-6 py-4 font-bold text-gray-900">{t('learnMorePage.featureSpeed')}</td>
                      <td className="px-6 py-4 text-gray-600">{t('learnMorePage.mattersSpeed')}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-bold text-gray-900">{t('learnMorePage.featureCost')}</td>
                      <td className="px-6 py-4 text-gray-600">{t('learnMorePage.mattersCost')}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-bold text-gray-900">{t('learnMorePage.featureQuality')}</td>
                      <td className="px-6 py-4 text-gray-600">{t('learnMorePage.mattersQuality')}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-bold text-gray-900">{t('learnMorePage.featureOpen')}</td>
                      <td className="px-6 py-4 text-gray-600">{t('learnMorePage.mattersOpen')}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-bold text-gray-900">{t('learnMorePage.featureTrans')}</td>
                      <td className="px-6 py-4 text-gray-600">{t('learnMorePage.mattersTrans')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Bottom Line */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('learnMorePage.bottomTitle')}
              </h2>
              <p>{t('learnMorePage.bottomIntro')}</p>
              <p>{t('learnMorePage.bottomOutro1')}</p>
              <p className="font-semibold">{t('learnMorePage.bottomOutro2')}</p>
            </section>

            {/* Callout Footer */}
            <section className="bg-gradient-to-tr from-slate-900 to-sky-950 text-white p-8 rounded-3xl space-y-4 shadow-xl">
              <p className="text-xl font-bold text-sky-400">
                {t('learnMorePage.finalSection1')}
              </p>
              <p className="text-gray-200 leading-relaxed font-light">
                {t('learnMorePage.finalSection2')}
              </p>
            </section>
          </div>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default LearnMorePage;
